/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AiringOffer } from 'client-code/airing';
import {
    StationPolicy,
    StreamingDeviceType,
    StreamingRestrictionType,
    StreamingRestrictionsInternal,
} from 'client-code/channels-service';
import { PlaybackRestrictionType } from './enums';
import { Constants } from '../constants';
import { QoEAppLogger } from '../logging/qoe/QoEAppLogger';
import { Storage } from '../storage/Storage';
import { QoERegionModeType } from '../logging/qoe/QoERegionModeType';
import { TOfferPreviewCustomData } from '../types/TOfferPreviewCustomData';
import { DeviceModel } from '../device';
import { PlaybackContentType } from '../socu/enums/PlaybackContentType';
import { IPlayable } from '../interfaces';

export class PlaybackRestriction {
    public checkAllRestriction = (
        playable: IPlayable,
        airingData: AiringOffer[],
        previewData: TOfferPreviewCustomData,
    ): PlaybackRestrictionType => {
        return this.checkRestriction(playable, airingData, previewData);
    };

    public checkCanPlayStartRestriction = (playable: IPlayable): PlaybackRestrictionType => {
        return this.checkRestriction(playable);
    };

    // eslint-disable-next-line class-methods-use-this
    private checkDeviceTypeRestriction(
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
    ): boolean {
        if (policyInfo && policyInfo.length > 0) {
            for (let i = 0; i < policyInfo.length; i += 1) {
                const streamingRules = policyInfo[i]?.linearStationRules?.streamingRules;
                const linearStreamingRestrictions = streamingRules?.linearStreamingRestrictions;
                if (
                    playbackContentType === PlaybackContentType.LIVE &&
                    this.isDeviceRestricted(linearStreamingRestrictions)
                ) {
                    return true;
                }
                const soStreamingRestrictions = streamingRules?.soStreamingRestrictions;
                const cuStreamingRestrictions = streamingRules?.cuStreamingRestrictions;
                if (
                    playbackContentType !== PlaybackContentType.LIVE &&
                    this.isDeviceRestricted(soStreamingRestrictions) &&
                    this.isDeviceRestricted(cuStreamingRestrictions)
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    private checkInBlacklist = (): boolean => {
        const inHomeLocationData = Storage.getInstance().getItem(
            Storage.Keys.IN_HOME_LOCATION_DATA,
        );
        if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            const inBlacklist = inHomeLocationData.response?.inBlacklist as boolean;
            if (inBlacklist) {
                QoEAppLogger.logDeviceBlacklisted();
            }
            return inBlacklist;
        }
        return false;
    };

    /**
     * @param airingData
     * @param previewData
     * @returns true when program is true, false otherwise
     * If,anyhow airingdata is missing, we have to fallback on preview
     */
    private checkIsAdult = (
        airingData: AiringOffer[],
        previewData: TOfferPreviewCustomData,
    ): boolean => {
        if (airingData.length > 0) {
            return airingData[0]?.parentalControls?.isAdult ?? false;
        }
        return !!previewData?.contentPreview?.isAdult;
    };

    private checkOutOfHomeRestriction = (
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
    ): boolean => {
        let isInHome = true;
        const inHomeLocationData = Storage.getInstance().getItem(
            Storage.Keys.IN_HOME_LOCATION_DATA,
        );
        if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            isInHome = inHomeLocationData.response?.inHome as boolean;
        }

        if (isInHome) return false;

        if (policyInfo && policyInfo.length > 0) {
            for (let i = 0; i < policyInfo.length; i += 1) {
                const streamingRules = policyInfo[i]?.linearStationRules?.streamingRules;
                switch (playbackContentType) {
                    case PlaybackContentType.CATCHUP_BEGINNING:
                    case PlaybackContentType.CATCHUP_RESUME:
                        if (
                            streamingRules?.cuStreamingRestrictions?.outOfHomeStreaming?.includes(
                                StreamingRestrictionType.Blocked,
                            )
                        ) {
                            return true;
                        }
                        break;

                    case PlaybackContentType.STARTOVER_BEGINNING:
                    case PlaybackContentType.STARTOVER_RESUME:
                        if (
                            streamingRules?.soStreamingRestrictions?.outOfHomeStreaming?.includes(
                                StreamingRestrictionType.Blocked,
                            )
                        ) {
                            return true;
                        }
                        break;

                    default:
                        if (
                            streamingRules?.linearStreamingRestrictions?.outOfHomeStreaming?.includes(
                                StreamingRestrictionType.Blocked,
                            )
                        ) {
                            return true;
                        }
                }
            }
        }
        return false;
    };

    private checkOutOfRegionRestriction = (): boolean => {
        const inHomeLocationData = Storage.getInstance().getItem(
            Storage.Keys.IN_HOME_LOCATION_DATA,
        );
        if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            const isInRegion = inHomeLocationData.response?.inRegion as boolean;
            if (!isInRegion) return true;
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.OUT);
        }

        if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.ERROR) {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.ERROR);
        } else if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE) {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.IN_GRACE_PERIOD);
        } else {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.IN);
        }
        return false;
    };

    private checkRestriction = (
        playable: IPlayable,
        airingData?: AiringOffer[],
        previewData?: TOfferPreviewCustomData,
    ): PlaybackRestrictionType => {
        const { channel, playbackContentType } = playable;
        const { isSubscribed, policyInfo } = channel;
        if (this.checkInBlacklist()) return PlaybackRestrictionType.Blacklisted;

        if (this.checkOutOfRegionRestriction()) return PlaybackRestrictionType.OutOfRegion;

        if (policyInfo && this.checkOutOfHomeRestriction(policyInfo, playbackContentType))
            return PlaybackRestrictionType.OutOfHome;

        if (policyInfo && this.checkDeviceTypeRestriction(policyInfo, playbackContentType))
            return PlaybackRestrictionType.DeviceRestricted;

        if (airingData && previewData && this.checkIsAdult(airingData, previewData))
            return PlaybackRestrictionType.Adult;

        if (
            playable.playbackContentType !== PlaybackContentType.LIVE &&
            previewData?.contentPreview?.isAdult
        )
            return PlaybackRestrictionType.Adult;

        if (!isSubscribed) return PlaybackRestrictionType.Unsubscribed;
        return PlaybackRestrictionType.NoRestriction;
    };

    // eslint-disable-next-line class-methods-use-this
    private isDeviceRestricted(
        streamingRestrictions: StreamingRestrictionsInternal | undefined,
    ): boolean {
        if (
            streamingRestrictions?.restrictedDeviceType?.includes(
                <StreamingDeviceType>DeviceModel.getTiVoSettings().getDeviceType(),
            ) &&
            !streamingRestrictions.checkDeviceTypeWithMso
        ) {
            return true;
        }
        return false;
    }
}
